import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Logo, { DOCKHTA_LOGOS } from '@/components/Logo';
import { 
  Search, 
  CalendarCheck, 
  Users, 
  Shield, 
  Award, 
  Star, 
  CheckCircle, 
  ArrowRight, 
  MessageCircle,
  Clock,
  Loader2,
  UserPlus
} from 'lucide-react';
import { toast } from 'sonner';

const JoinProfessionals = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    profession: '',
    specialty: '',
    city: '',
    establishment: '',
    whatsapp: '',
    email: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const professions = [
    'Médecin généraliste',
    'Médecin spécialiste',
    'Chirurgien',
    'Dentiste',
    'Pharmacien',
    'Infirmier(ère)',
    'Sage-femme',
    'Kinésithérapeute',
    'Psychologue',
    'Nutritionniste',
    'Opticien',
    'Laborantin',
    'Autre'
  ];

  const specialties = [
    'Cardiologie',
    'Dermatologie',
    'Gynécologie',
    'Pédiatrie',
    'Ophtalmologie',
    'ORL',
    'Neurologie',
    'Psychiatrie',
    'Radiologie',
    'Chirurgie générale',
    'Orthopédie',
    'Urologie',
    'Gastro-entérologie',
    'Pneumologie',
    'Endocrinologie',
    'Rhumatologie',
    'Autre'
  ];

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.fullName || !formData.profession || !formData.city || !formData.whatsapp) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    const whatsappRegex = /^[+]?[\d\s-]{8,}$/;
    if (!whatsappRegex.test(formData.whatsapp)) {
      toast.error('Veuillez entrer un numéro WhatsApp valide');
      return;
    }

    setLoading(true);

    const message = `🏥 *Nouvelle inscription Professionnel Fondateur DOCKHTA*

👤 *Nom:* ${formData.fullName}
💼 *Profession:* ${formData.profession}
${formData.specialty ? `🎯 *Spécialité:* ${formData.specialty}` : ''}
📍 *Ville:* ${formData.city}
${formData.establishment ? `🏨 *Établissement:* ${formData.establishment}` : ''}
📱 *WhatsApp:* ${formData.whatsapp}
${formData.email ? `📧 *Email:* ${formData.email}` : ''}

_Envoyé depuis le formulaire de recrutement DOCKHTA_`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappNumber = '15144438084';
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;

    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
      window.open(whatsappUrl, '_blank');
      toast.success('Redirection vers WhatsApp...');
    }, 1000);
  };

  const benefits = [
    {
      icon: Search,
      title: 'Plus de visibilité',
      description: 'Soyez trouvé facilement par des patients près de chez vous.'
    },
    {
      icon: CalendarCheck,
      title: 'Gestion simplifiée',
      description: 'Gérez vos disponibilités et vos rendez-vous en toute simplicité.'
    },
    {
      icon: Users,
      title: 'Nouveaux patients',
      description: 'Développez votre activité et augmentez votre patientèle.'
    },
    {
      icon: Shield,
      title: 'Fiable et sécurisé',
      description: 'Vos informations et celles de vos patients sont protégées.'
    }
  ];

  const founderAdvantages = [
    'Badge « Professionnel Fondateur DOCKHTA »',
    'Mise en avant prioritaire lors du lancement',
    'Accompagnement personnalisé pour la création du profil',
    'Accès gratuit aux fonctionnalités premium pendant la période de lancement',
    'Participation aux évolutions futures de la plateforme'
  ];

  const steps = [
    { number: 1, text: 'Remplissez le formulaire ci-dessous' },
    { number: 2, text: 'Notre équipe vous contactera sur WhatsApp' },
    { number: 3, text: 'Nous vous accompagnerons dans la création de votre profil professionnel' },
    { number: 4, text: 'Vous rejoindrez la communauté des Professionnels Fondateurs DOCKHTA' }
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0D47A1] to-[#1565C0] flex items-center justify-center px-4">
        <Card className="max-w-lg w-full border-0 shadow-2xl">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Merci pour votre intérêt !
            </h2>
            <p className="text-slate-600 mb-6">
              Vous allez être redirigé vers WhatsApp pour finaliser votre inscription. 
              Si la redirection n&apos;a pas fonctionné, cliquez sur le bouton ci-dessous.
            </p>
            <div className="space-y-3">
              <Button 
                onClick={() => {
                  const message = `Bonjour ! Je souhaite rejoindre les Professionnels Fondateurs DOCKHTA.\n\nNom: ${formData.fullName}\nProfession: ${formData.profession}\nVille: ${formData.city}\nWhatsApp: ${formData.whatsapp}`;
                  window.open(`https://wa.me/15144438084?text=${encodeURIComponent(message)}`, '_blank');
                }}
                className="w-full bg-[#25D366] hover:bg-[#128C7E] h-12"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Ouvrir WhatsApp
              </Button>
              <Link to="/">
                <Button variant="outline" className="w-full">
                  Retour à l&apos;accueil
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Matching brand design */}
      <section className="relative bg-gradient-to-br from-[#E8F4FD] via-white to-[#F0F8FF] overflow-hidden">
        {/* Header */}
        <header className="px-4 py-4 md:py-6">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Logo variant="default" size="sm" showSlogan={false} />
            </Link>
            <Link to="/login">
              <Button variant="outline" className="border-[#0D47A1] text-[#0D47A1] hover:bg-[#0D47A1] hover:text-white">
                Connexion
              </Button>
            </Link>
          </div>
        </header>

        {/* Hero Content */}
        <div className="max-w-7xl mx-auto px-4 py-8 md:py-16">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Text Content */}
            <div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <span className="text-[#0D47A1]">Ensemble, améliorons</span>
                <br />
                <span className="text-[#00BCD4] italic">l&apos;accès aux soins.</span>
              </h1>
              
              <p className="text-base md:text-lg text-slate-600 mb-8 leading-relaxed max-w-xl">
                DOCKHTA est une plateforme de santé innovante qui connecte les patients aux professionnels de santé et simplifie la prise de rendez-vous.
              </p>

              {/* Founder CTA */}
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 md:p-6 border border-[#0D47A1]/10 shadow-lg mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#0D47A1]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <UserPlus className="w-6 h-6 text-[#0D47A1]" />
                  </div>
                  <div>
                    <p className="text-[#0D47A1] font-medium">Rejoignez les</p>
                    <p className="text-[#0D47A1] font-bold text-lg">Professionnels Fondateurs</p>
                    <p className="text-slate-500 text-sm">et faites partie du changement !</p>
                  </div>
                </div>
              </div>

              <Button 
                size="lg"
                onClick={() => document.getElementById('form-section').scrollIntoView({ behavior: 'smooth' })}
                className="bg-[#00BCD4] hover:bg-[#00ACC1] text-white h-12 md:h-14 px-6 md:px-8 rounded-full text-base md:text-lg shadow-lg hover:shadow-xl transition-all"
              >
                Je rejoins le programme
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>

            {/* Hero Image */}
            <div className="relative order-first lg:order-last">
              <img 
                src="https://images.unsplash.com/photo-1666887360388-93e684b6474a?w=800&q=80"
                alt="Professionnels de santé DOCKHTA"
                className="w-full h-auto rounded-2xl shadow-xl"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&q=80';
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10 md:mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0D47A1] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Pourquoi rejoindre DOCKHTA dès maintenant ?
            </h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <Card key={index} className="border-0 shadow-md hover:shadow-lg transition-shadow bg-white text-center">
                  <CardContent className="p-4 md:p-6">
                    <div className="w-12 h-12 md:w-14 md:h-14 bg-[#0D47A1]/10 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                      <IconComponent className="w-6 h-6 md:w-7 md:h-7 text-[#0D47A1]" />
                    </div>
                    <h3 className="font-semibold text-[#0D47A1] mb-2 text-sm md:text-base">{benefit.title}</h3>
                    <p className="text-xs md:text-sm text-slate-600">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Founder Advantages Section */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-[#0D47A1] to-[#1565C0] text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <Award className="w-4 h-4 text-yellow-400" />
                <span className="text-sm font-medium">Avantages exclusifs</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Avantages réservés aux Professionnels Fondateurs
              </h2>
              <p className="text-white/80 mb-8 text-sm md:text-base">
                En rejoignant DOCKHTA dès maintenant, vous bénéficiez d&apos;avantages exclusifs réservés à nos premiers partenaires de santé.
              </p>

              <div className="space-y-3 md:space-y-4">
                {founderAdvantages.map((advantage, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-5 h-5 md:w-6 md:h-6 bg-[#00BCD4] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-white" />
                    </div>
                    <span className="text-white/90 text-sm md:text-base">{advantage}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Badge Preview */}
            <div className="flex justify-center">
              <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-6 md:p-8 max-w-sm w-full">
                <div className="text-center">
                  <div className="w-20 h-20 md:w-24 md:h-24 bg-gradient-to-br from-[#0D47A1] to-[#1565C0] rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6 shadow-lg border-4 border-white/30">
                    <Star className="w-10 h-10 md:w-12 md:h-12 text-white" />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold mb-2">Professionnel Fondateur</h3>
                  <p className="text-white/70 text-sm mb-4">Badge exclusif DOCKHTA</p>
                  <div className="bg-white/10 rounded-xl p-4">
                    <p className="text-xs md:text-sm text-white/80">
                      Ce badge sera affiché sur votre profil pour vous identifier comme membre fondateur de la communauté DOCKHTA.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to Join Section */}
      <section className="py-12 md:py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-10 md:mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0D47A1] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Comment participer ?
            </h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="flex flex-col items-center text-center">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-[#00BCD4] rounded-full flex items-center justify-center mb-3 md:mb-4 shadow-lg">
                    <span className="text-white font-bold text-base md:text-lg">{step.number}</span>
                  </div>
                  <p className="text-slate-700 text-xs md:text-sm">{step.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section id="form-section" className="py-12 md:py-20 bg-white">
        <div className="max-w-xl mx-auto px-4">
          <Card className="border-0 shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-[#0D47A1] to-[#1565C0] p-4 md:p-6 text-white text-center">
              <div className="w-12 h-12 md:w-14 md:h-14 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <UserPlus className="w-6 h-6 md:w-7 md:h-7" />
              </div>
              <h2 className="text-lg md:text-xl font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Formulaire d&apos;inscription
              </h2>
              <p className="text-white/80 text-xs md:text-sm">
                Rejoignez les Professionnels Fondateurs DOCKHTA
              </p>
            </div>

            <CardContent className="p-4 md:p-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Nom et prénom */}
                <div>
                  <Label htmlFor="fullName" className="text-slate-700 text-sm">
                    Nom et prénom <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="fullName"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    placeholder="Dr. Jean Dupont"
                    className="mt-1 h-11"
                    required
                  />
                </div>

                {/* Profession */}
                <div>
                  <Label htmlFor="profession" className="text-slate-700 text-sm">
                    Profession <span className="text-red-500">*</span>
                  </Label>
                  <Select 
                    value={formData.profession} 
                    onValueChange={(v) => setFormData({ ...formData, profession: v })}
                  >
                    <SelectTrigger className="mt-1 h-11">
                      <SelectValue placeholder="Sélectionnez votre profession" />
                    </SelectTrigger>
                    <SelectContent>
                      {professions.map((prof) => (
                        <SelectItem key={prof} value={prof}>{prof}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Spécialité */}
                <div>
                  <Label htmlFor="specialty" className="text-slate-700 text-sm">
                    Spécialité <span className="text-slate-400">(optionnel)</span>
                  </Label>
                  <Select 
                    value={formData.specialty} 
                    onValueChange={(v) => setFormData({ ...formData, specialty: v })}
                  >
                    <SelectTrigger className="mt-1 h-11">
                      <SelectValue placeholder="Sélectionnez votre spécialité" />
                    </SelectTrigger>
                    <SelectContent>
                      {specialties.map((spec) => (
                        <SelectItem key={spec} value={spec}>{spec}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Ville */}
                <div>
                  <Label htmlFor="city" className="text-slate-700 text-sm">
                    Ville <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Ex: Yaoundé, Douala..."
                    className="mt-1 h-11"
                    required
                  />
                </div>

                {/* Établissement */}
                <div>
                  <Label htmlFor="establishment" className="text-slate-700 text-sm">
                    Établissement de santé <span className="text-slate-400">(optionnel)</span>
                  </Label>
                  <Input
                    id="establishment"
                    value={formData.establishment}
                    onChange={(e) => setFormData({ ...formData, establishment: e.target.value })}
                    placeholder="Nom de votre clinique ou hôpital"
                    className="mt-1 h-11"
                  />
                </div>

                {/* WhatsApp */}
                <div>
                  <Label htmlFor="whatsapp" className="text-slate-700 text-sm">
                    Numéro WhatsApp <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="whatsapp"
                    value={formData.whatsapp}
                    onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                    placeholder="+237 6XX XXX XXX"
                    className="mt-1 h-11"
                    required
                  />
                </div>

                {/* Email */}
                <div>
                  <Label htmlFor="email" className="text-slate-700 text-sm">
                    Adresse e-mail <span className="text-slate-400">(optionnel)</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="votre@email.com"
                    className="mt-1 h-11"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="join-program-submit-btn"
                  className="w-full bg-[#00BCD4] hover:bg-[#00ACC1] h-12 md:h-14 text-base md:text-lg rounded-full mt-4"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      Je rejoins le programme
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>

                {/* Note */}
                <p className="text-center text-xs md:text-sm text-slate-500 mt-4 flex items-center justify-center gap-1">
                  <Clock className="w-4 h-4" />
                  Places limitées pour la phase pilote
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Bottom CTA Bar */}
      <section className="bg-[#0D47A1] py-4 md:py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-white/10 rounded-full flex items-center justify-center">
                <Star className="w-5 h-5 md:w-6 md:h-6 text-white" />
              </div>
              <div className="text-white text-center md:text-left">
                <p className="font-semibold text-sm md:text-base">Professionnel Fondateur DOCKHTA</p>
                <p className="text-white/70 text-xs md:text-sm">Accès gratuit aux fonctionnalités premium pendant le lancement.</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-white text-center md:text-left">
                <p className="font-semibold text-sm md:text-base">Places limitées !</p>
                <p className="text-white/70 text-xs md:text-sm">Rejoignez dès maintenant les premiers professionnels de santé partenaires.</p>
              </div>
              <Button 
                onClick={() => document.getElementById('form-section').scrollIntoView({ behavior: 'smooth' })}
                className="bg-[#00BCD4] hover:bg-[#00ACC1] text-white rounded-full px-4 md:px-6 h-10 md:h-12 whitespace-nowrap"
              >
                Je rejoins le programme
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0D1B2A] text-white py-6 md:py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2">
              <Logo variant="icon" size="xs" />
              <span className="font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>DOCKHTA</span>
            </Link>
            <p className="text-slate-400 text-xs md:text-sm text-center">
              © 2026 DOCKHTA. Tous droits réservés.
            </p>
            <div className="flex items-center gap-4 text-xs md:text-sm">
              <Link to="/terms" className="text-slate-400 hover:text-white transition-colors">
                CGU
              </Link>
              <Link to="/privacy" className="text-slate-400 hover:text-white transition-colors">
                Confidentialité
              </Link>
              <Link to="/contact" className="text-slate-400 hover:text-white transition-colors">
                Contact
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default JoinProfessionals;
